import moment from 'moment-timezone';

export class DateTimeHelperTZ {
  public static removeTimezone(dateStr: string): string {
    return dateStr.replace('Z', '');
  }

  public static minutesToDate(minutes: number, referenceDate?: Date): DSTGapInfo {
    if (minutes < 0) {
      throw new Error(`Invalid minutes: ${minutes}`);
    }

    const timeZone = moment.tz.guess();
    const ref = referenceDate ? moment(referenceDate).tz(timeZone) : moment.tz(timeZone);
    const midnight = ref.clone().startOf('day');
    const result = midnight.clone().add(minutes, 'minutes');
    let gapType = DSTGapType.NoGap;

    const gap = DateTimeHelperTZ.getDSTGapInfo(referenceDate, timeZone);

    if (gap) {
      if (minutes >= gap.endMinute) {
        gapType = DSTGapType.AfterGap;
      } else if (minutes < gap.startMinute) {
        gapType = DSTGapType.BeforeGap;
      } else if (minutes >= gap.startMinute && minutes < gap.endMinute) {
        gapType = DSTGapType.WithinGap;
      }
    }

    return { date: result.toDate(), gapType, winterToSummer: gap?.winterToSummer };
  }

  public static getDSTGapInfo(referenceDate?: Date, timeZone?: string): DSTGapCalculation | null {
    const tz = timeZone || moment.tz.guess();
    const ref = referenceDate ? moment(referenceDate).tz(tz) : moment.tz(tz);
    const midnight = ref.clone().startOf('day');
    const startOffset = midnight.utcOffset();

    for (let m = 1; m <= 1440; m++) {
      const probe = midnight.clone().add(m, 'minutes');
      const probeOffset = probe.utcOffset();

      if (probeOffset !== startOffset) {
        const delta = probeOffset - startOffset;

        if (delta > 0) {
          return {
            startMinute: m,
            endMinute: m + delta,
            sizeMinutes: delta,
            winterToSummer: true,
          };
        } else if (delta < 0) {
          return {
            startMinute: m,
            endMinute: m,
            sizeMinutes: delta,
            winterToSummer: false,
          };
        }

        return null;
      }
    }

    return null;
  }
}

export enum DSTGapType {
  WithinGap, // Within calendar week day within DST hour (e.g. 2026-03-29 between 02:00am and within 02:59am)
  BeforeGap, // Within calendar week day before DST hour (e.g. 2026-03-29 between 00:00am and 01:59am)
  AfterGap, // Within calendar week day after DST hour (e.g. 2026-09-29 between 03:00am and 11:59pm)
  NoGap, // Not a day when DST occurs.
}

export type DSTGapInfo = {
  date: Date;
  gapType: DSTGapType;
  winterToSummer: boolean;
};

type DSTGapCalculation = {
  startMinute: number;
  endMinute: number;
  sizeMinutes: number;
  winterToSummer: boolean;
};
